import axios from 'axios'
import CacheStorageService from './cacheStorage.service'

class DigitalVaultDocumentService {
    constructor(apiUrl) {
        this.apiUrl = apiUrl
        this.cache = new CacheStorageService()
        this.cacheUrls = [
            '/categories-by-company',
            '/documents-by-subcategory'
        ]
    }

    /**
    * @author Edgar Escobedo
    * @description GET document info
    * @date 03-11-2023
    * @param {String} documentId
    * @param {String} timezone
    * @param {String} token
    * @returns {Object}
    * @memberof DigitalVaultDocumentService
    */
    async getDocumentData(documentId, timezone, token) {
        let response = await axios.get(`${this.apiUrl}/registration-document-vault?documentId=${documentId}&timezone=${encodeURIComponent(timezone)}`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })

        return response.data
    }

    /**
   * @author Nestor Sandoval
   * @description GET complete document info by ID (includes all related data)
   * @date 20-10-2025
   * @param {String} documentId
   * @param {String} token
   * @returns {Object}
   * @memberof DigitalVaultDocumentService
   */
    async getDocumentById(documentId, token) {
        let response = await axios.get(`${this.apiUrl}/vault-document?id=${documentId}`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })

        return response.data
    }

    /**
    * @author Edgar Escobedo
    * @description Update document vault with all data
    * @date 08-11-2023
    * @updatedBy Adriana Fernandez 04-12-23
    * @param {Number} id
    * @param {String} description
    * @param {Date} startDate
    * @param {Date} validation
    * @param {Number} amount
    * @param {Boolean} confidential
    * @param {Boolean} mandatory
    * @param {Boolean} penalty
    * @param {Array} tags
    * @param {String} token
    * @param {Number | String} monetaryUnit
    * @returns {Object}
    * @memberof DigitalVaultDocumentService
    */
    async updateDocumentVault(id, alias, description, startDate, validation, amount, confidential, notify, mandatory, penalty, tags, entities, workflowExecutionId, variables, token, name, categoryId, companyId, pathBreadcrumb, workflowEXP, monetaryUnit, typeDocument, sendFilesToOCR, dataFilesToOCR, sendFilesToOCRValue) {
        const normalizedTags = Array.isArray(tags) ? tags : []
        const normalizedVariables = Array.isArray(variables) ? variables : []
        const normalizedEntities = Array.isArray(entities) ? { values: entities } : (entities && typeof entities === 'object' ? entities : { values: [] })
        const normalizedCategoryId = (categoryId === undefined) ? null : categoryId
        const normalizedCompanyId = (companyId === undefined) ? null : companyId
        const normalizedPath = typeof pathBreadcrumb === 'string' ? pathBreadcrumb : (pathBreadcrumb ? String(pathBreadcrumb) : '')
        const normalizedDataFilesToOCR = Array.isArray(dataFilesToOCR) ? dataFilesToOCR : []

        const mappedVariables = normalizedVariables.map(v => {
            if (!v) return v
            let value = v.valueVariable
            if (v.typeData === 'date') {
                if (value instanceof Date) {
                    value = value.toISOString()
                } else if (typeof value === 'string') {
                    if (value.length === 0) value = ''
                    else {
                        const d = new Date(value)
                        value = isNaN(d.getTime()) ? '' : d.toISOString()
                    }
                } else {
                    value = ''
                }
            }
            return { ...v, valueVariable: value }
        })

        let data = {
            id,
            alias,
            description,
            startDate,
            validation,
            amount,
            confidential,
            notify,
            mandatory,
            penalty,
            tags: normalizedTags,
            entities: normalizedEntities,
            workflowExecutionId,
            variables: mappedVariables,
            categoryId: normalizedCategoryId,
            companyId: normalizedCompanyId,
            pathBreadcrumb: normalizedPath,
            workflowEXP,
            monetaryUnit,
            typeDocument,
            sendFilesToOCRValue
        }
        if (name) {
            data.name = name
        }

        if (sendFilesToOCR) {
            data.sendFilesToOCR = sendFilesToOCR
            data.dataFilesToOCR = normalizedDataFilesToOCR
        }

        let response = await axios.post(`${this.apiUrl}/registration-document-vault`,
            data,
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })

        const cache = new CacheStorageService()
        cache.deleteUrlFromCache('/categories-by-company')
        cache.deleteUrlFromCache('/documents-by-subcategory')

        return response.data
    }

    /**
   * @author Edgar Escobedo
   * @updatedBy Adriana Fernandez
   * @description Update document status
   * @date 17-11-2023
   * @param {Array} documentIds
   * @param {String} status
   * @param {String} token
   * @returns {Object}
   * @memberof DigitalVaultDocumentService
   */
    async updateDocumentStatus(documentIds, status, token, fileId) {
        if (!Array.isArray(documentIds)) {
            documentIds = [documentIds]
        }
        let response = await axios.post(`${this.apiUrl}/delete-restore-document`, {
            documentIds, status, fileId
        },
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })

        this.cacheUrls.forEach(url => {
            this.cache.deleteUrlFromCache(url)
        })

        return response.data
    }

    /**
    * @author Edgar Escobedo
    * @description Version a document
    * @date 23-11-2023
    * @param {UUID} documentId
    * @param {Data} documento
    * @param {String} token
    * @returns {Object}
    * @memberof DigitalVaultDocumentService
    */
    async versionDocument(documento, token) {
        let response = await axios.post(
            `${this.apiUrl}/version-document`,
            documento,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data;
    }

    /**
   * @author Edgar Escobedo
   * @description Make a version mainDoc
   * @date 28-11-2023
   * @param {UUID} documentId
   * @param {String} fileId
   * @param {String} token
   * @returns {Object}
   * @memberof DigitalVaultDocumentService
   */
    async makeVersionMainDoc(documentId, fileId, mainDoc, token) {
        let response = await axios.post(
            `${this.apiUrl}/make-version-main-doc`,
            { documentId, fileId, mainDoc },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data
    }

    /**
  * @author Pedro Antonio
  * @description Renew a document by documentId
  * @date 3-12-2023
  * @param {UUID} documentId
  * @param {UUID} companyId
  * @param {File} renewedDocument
  * @param {String} token
  * @returns {Object}
  * @memberof DigitalVaultDocumentService
  */
    async renewDocument(renewedDocumentData, token) {
        let response = await axios.put(
            `${this.apiUrl}/renew-document`,
            renewedDocumentData,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data
    }


    /**
    * @author Edgar Escobedo
    * @description GET all permissionRole
    * @date 02-02-2024
    * @param {String} token
    * @returns {Object}
    * @memberof DigitalVaultDocumentService
    */
    async getAllPermissionRole(token) {
        let response = await axios.get(`${this.apiUrl}/permission-role`,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })

        return response.data
    }

    /**
    * @author Edgar Escobedo
    * @description add new file to an Expediente
    * @date 16-05-2024
    * @param {String} token
    * @returns {Object}
    * @memberof DigitalVaultDocumentService
    */
    async addFileToExpediente(documentData, token) {
        let response = await axios.post(`${this.apiUrl}/add-file-expediente`,
            documentData,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }

        )

        return response.data
    }

    /**
    * @author Edgar Escobedo
    * @description Reject document
    * @date 24-07-2024
    * @param {UIDD} documentId
    * @param {String} token
    * @returns {Object}
    * @memberof DigitalVaultDocumentService
    */
    async rejectDocument(documentId, token) {
        let response = await axios.post(`${this.apiUrl}/reject-document`,
            { documentId },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }

        )

        return response.data
    }

    /**
    * @author Edgar Escobedo
    * @description GET document information and pages
    * @date 26-08-2024
    * @param {UIDD} documentId
    * @param {String} token
    * @returns {Object}
    * @memberof DigitalVaultDocumentService
    */
    async getDocumentPages(documentId, token) {
        let response = await axios.get(`${this.apiUrl}/document-pages?documentId=${documentId}`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })

        return response.data
    }

    /**
     * @author Edgar Escobedo
     * @description Send document to OCR to extract entities
     * @createdDate 03-09-2025
     * @param {String} documentId - Document id to extract entities.
     * @param {[{id:Number, name:String, pages:Number, process:String, unitary:String, credits:String, value:Boolean}]} documentsToOCR - List of documents to send to OCR.
     * @param {String} token - User token to authorize request.
     * @memberof DigitalVaultDocumentService
     * @returns {Promise<{status: Number, message:String: data: {}}>}
     */
    async sendEntitiesToExtract(documentId, documentsToOCR, token) {
        let response = await axios.post(`${this.apiUrl}/extract-entities`,
            { documentId, documentsToOCR }, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })

        return response.data
    }

    /**
     * @author Marissa Vargas
     * @description Download file by documentId
     * @date 29-08-2024
     * @param {string} documentId - ID of the document to download
     * @param {string} token - Authorization token
     * @returns {Object} - Response data containing the file
     * @memberof DigitalVaultDocumentService
     */
    async downloadFile({ documentId, token }) {
        const url = new URL(`${this.apiUrl}/download-document`)
        url.searchParams.append('documentId', documentId)

        const response = await axios.get(url.toString(), {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
    }

    /**
     * @author Nestor Sandoval
     * @description Get document messages by workflowExecutionId
     * @date 05-02-2026
     * @param {String} documentId - El workflowExecutionId del documento
     * @param {String} timezone 
     * @param {String} token
     * @returns {Object} 
     */
    async getDocumentMessages(documentId, timezone, token) {
        let response = await axios.get(
            `${this.apiUrl}/workflow-execution-messages?workflowExecutionId=${documentId}&timezone=${encodeURIComponent(timezone)}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        )
        return response.data
    }

    /**
     * @author Nestor Sandoval
     * @description 
     * @date 05-02-2026
     * @param {Object} messageConversation 
     * @param {String} token
     * @returns {Object}
     */
    async createDocumentMessage(messageConversation, token) {
        let response = await axios.post(
            `${this.apiUrl}/workflow-execution-new-message`,
            messageConversation,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        )
        return response.data
    }

    /**
     * @author Nestor Sandoval
     * @description Get external user documents or applicant documents and users
     * @date 06-02-2026
     * @param {String} documentId - ID del documento
     * @param {String} token - Token de autorizaci\u00f3n
     * @returns {Promise<{statusCode: number, message: string, data: {user: Object, usersDocuments: Array<{recipientId: number, userEmail: string, userId: number, documentName: string, documents: Array}>, userIsApplicant: boolean}}>}
     * @memberof DigitalVaultDocumentService
     */
    async getExternalDocuments(documentId, token) {
        const response = await axios.get(
            `${this.apiUrl}/external-documents?documentId=${documentId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        )
        return response.data
    }

    /**
     * @author Nestor Sandoval
     * @description Subir documento externo solicitado
     * @date 06-02-2026
     * @param {FormData} formData - FormData con document, documentId, recipientId, templateId, requestItemId, isApplicant
     * @param {String} token - Token de autorizaci\u00f3n
     * @returns {Promise<{statusCode: number, message: string, data: Object}>}
     * @memberof DigitalVaultDocumentService
     */
    async uploadExternalDocument(formData, token) {
        const response = await axios.post(
            `${this.apiUrl}/external-documents`,
            formData,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            }
        )
        return response.data
    }

    /**
     * @author Nestor Sandoval
     * @description Eliminar documento externo solicitado
     * @date 10-02-2026
     * @param {String} documentId - ID del documento principal
     * @param {Number} recipientId - ID del destinatario
     * @param {Number} requestItemId - ID del item de solicitud
     * @param {String} token - Token de autorizaci\u00f3n
     * @returns {Promise<{statusCode: number, message: string, data: Object}>}
     * @memberof DigitalVaultDocumentService
     */
    async deleteExternalDocument(documentId, recipientId, requestItemId, token) {
        const response = await axios.delete(
            `${this.apiUrl}/external-documents?documentId=${documentId}&recipientId=${recipientId}&requestItemId=${requestItemId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        )
        return response.data
    }

    /**
     * @author Nestor Sandoval
     * @description Enviar notificación por correo electrónico sobre cambios en documentos externos
     * @date 11-02-2026
     * @param {String} documentId - ID del documento principal
     * @param {Number} recipientId - ID del destinatario
     * @param {String} notificationType - Tipo de notificación (UPLOADED, ACCEPTED, REJECTED)
     * @param {String} token - Token de autorizaci\u00f3n
     * @returns {Promise<{statusCode: number, message: string, data: Object}>}
     * @memberof DigitalVaultDocumentService
     */
    async sendExternalDocumentNotification(documentId, recipientId, notificationType, token) {
        const response = await axios.post(
            `${this.apiUrl}/external-documents-notifications`,
            {
                documentId,
                recipientId,
                notificationType
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        )
        return response.data
    }

    /**
     * @author Nestor Sandoval
     * @description Realizar acciones sobre documentos externos solicitados (aceptar, rechazar, marcar requerido/no requerido)
     * @date 13-02-2026
     * @param {String} documentId - ID del documento principal
     * @param {Number} recipientId - ID del destinatario
     * @param {Number} requestItemId - ID del item de solicitud
     * @param {String} action - Acción a realizar (UPLOADED, ACCEPTED, REJECTED, REQUIRED)
     * @param {Boolean} required - Si el documento es requerido o no (solo para action=REQUIRED)
     * @param {String} token - Token de autorización
     * @param {String} rejectionReason - Motivo de rechazo (solo para action=REJECTED)
     * @returns {Promise<{statusCode: number, message: string, data: Object}>}
     * @memberof DigitalVaultDocumentService
     */
    async performExternalDocumentAction(documentId, recipientId, requestItemId, action, required, token, comments = '') {
        const payload = {
            documentId,
            recipientId,
            requestItemId,
            action,
            required
        }

        // Agregar rejectionReason solo si la acción es REJECTED y se proporcionó
        if (action === 'REJECTED' && comments) {
            payload.comments = comments
        }

        const response = await axios.post(
            `${this.apiUrl}/external-documents-actions`,
            payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        )
        return response.data
    }

    /**
     * @author Nestor Sandoval
     * @description Cambiar el workflow de documentos externos (cancelar, cerrar, aprobar o rechazar solicitud)
     * @date 16-02-2026
     * @param {String} documentId - ID del documento principal (workflowExecutionId)
     * @param {String} action - Acción a realizar: 'cancel' (cancelar), 'close' (cerrar), 'next' (aprobar), 'back' (rechazar)
     * @param {String} token - Token de autorización
     * @returns {Promise<{statusCode: number, message: string, data: Object}>}
     * @memberof DigitalVaultDocumentService
     */
    async changeWorkflowGuestDocuments(documentId, action, token, comments = '') {
        const payload = {
            documentId
        }

        if (action === 'cancel') {
            payload.action = 'defined'
            payload.statusUpdate = 320799
        } else if (action === 'close') {
            payload.action = 'next'
            payload.statusUpdate = null
        } else if (action === 'next') {
            payload.action = 'next'
            payload.statusUpdate = null
        } else if (action === 'back') {
            payload.action = 'back'
            payload.statusUpdate = null
            payload.comments = comments
        }

        const response = await axios.post(
            `${this.apiUrl}/change-workflow-guest-documents`,
            payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        )
        return response.data
    }

    /**
     * @author Nestor Sandoval
     * @description Crear una nueva solicitud de workflow de documentos externos
     * @date 19-02-2026
     * @param {FormData} formData - FormData con los datos de la solicitud (usuarios, documentos, etc.)
     * @param {String} token - Token de autorización
     * @returns {Promise<{statusCode: number, message: string, data: Object}>}
     * @memberof DigitalVaultDocumentService
     */
    async createWorkflowGuestDocuments(formData, token) {
        const response = await axios.post(
            `${this.apiUrl}/workflow-guest-documents`,
            formData,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            }
        )
        return response.data
    }
}

export { DigitalVaultDocumentService as default }